// Roles and capabilities — THE single source (build plan §5).
//
// The server checks these on every protected route (403 + AccessDenied audit)
// and sends the user's capability list to the SPA, which renders only what the
// user may do. Nothing else in the app decides who may do what.
//
// `Licensed` and `MayAdministerOtc` are per-user flags INDEPENDENT of role
// (Decisions 2 #4); "Administered by" eligibility is computed from role AND
// flags below, not from role alone.
//
// Director = System Admin (Decisions 2 #5). There is no counselor role
// (Decisions 1). The model leaves room for a future reports-only role.

export const ROLES = ["SystemAdmin", "Supervisor", "RN", "MedStaff", "MedAdminStaff"] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  SystemAdmin: "System Admin",
  Supervisor: "Supervisor",
  RN: "RN",
  MedStaff: "Med staff",
  MedAdminStaff: "Med admin staff",
};

export const CAPABILITIES = [
  /** Users screen; config: insurance, contacts, signer, dosing authority. */
  "users.manage",
  "config.admin",
  /** Config: windows, cities, held/refused vocabulary. */
  "config.operational",
  "record.read",
  /** Read the applications into the roster now (the roster sweep, plan §7.1 #2). */
  "roster.sweep",
  /** Comment; flag MissingDocument / ClarificationNeeded; send doc request. */
  "record.comment",
  /** Flag ClinicalConcern / NeedsSupervisorReview. */
  "record.flagClinical",
  "record.markReviewed",
  "record.reReview",
  "hold.decide",
  /** "Run now" for the scheduled email jobs: reminders, Director digest (M2 Stage 3). */
  "jobs.run",
  /** Freeze the medication seed at amendments close (M3 §9.1 #1). */
  "seed.freeze",
  /** Assign cities: CSV import and manual override (M3 §9.1 #2). */
  "city.assign",
  /** Open visit, record receipt/exceptions, capture photos. */
  "station.use",
  /** Rx review + confirm medication; allergies confirm. */
  "rx.confirm",
  /** Path A in-app authorization with e-signature. */
  "authorization.complete",
  /** Path B: attach the remote provider's signed PDF. */
  "authorization.attach",
  /** Write the allergy response note (M3 Stage 3, owner decision 2026-10-16: RN or Supervisor). */
  "allergy.respond",
  "administration.enterOnBehalf",
  "visit.create",
  "notification.release",
  "restriction.set",
  /** A progress note on the chart (M4 §10.1 #5): RN, Supervisor, MedStaff. */
  "chart.progressNote",
  "supplies.manage",
  "supplies.dosing",
  "staff.import",
  "concern.add",
  "concern.close",
  "export.phi",
  "reports.screen",
] as const;
export type Capability = (typeof CAPABILITIES)[number];

const ALL: Role[] = [...ROLES];
const CAN: Record<Capability, readonly Role[]> = {
  "users.manage": ["SystemAdmin"],
  "config.admin": ["SystemAdmin"],
  "config.operational": ["SystemAdmin", "Supervisor", "RN"],
  "record.read": ALL,
  // An explicit System Admin action (Director = System Admin, D2 #5).
  "roster.sweep": ["SystemAdmin"],
  "record.comment": ALL,
  "record.flagClinical": ["SystemAdmin", "Supervisor", "RN", "MedStaff"],
  "record.markReviewed": ["SystemAdmin", "Supervisor", "RN"],
  "record.reReview": ["SystemAdmin", "Supervisor"],
  "hold.decide": ["SystemAdmin"],
  // Like roster.sweep: an explicit System Admin action.
  "jobs.run": ["SystemAdmin"],
  // §9.1 #1 names it a System Admin action.
  "seed.freeze": ["SystemAdmin"],
  // §5 does not name it; the roles that keep the city list (config.operational).
  "city.assign": ["SystemAdmin", "Supervisor", "RN"],
  "station.use": ALL,
  "rx.confirm": ["SystemAdmin", "Supervisor", "RN"],
  "authorization.complete": ["Supervisor"],
  // M3 Stage 2 (owner): RN and up attach the signed PDF and record the
  // provider's decision; it sets the order the medication pass gives.
  "authorization.attach": ["SystemAdmin", "Supervisor", "RN"],
  // Owner, 2026-10-16: "written by RN or Supervisor at review or check-in".
  "allergy.respond": ["Supervisor", "RN"],
  "administration.enterOnBehalf": ["SystemAdmin", "Supervisor", "RN", "MedStaff"],
  // MedStaff visits need a countersign (enforced with visits, Round M4).
  "visit.create": ["SystemAdmin", "Supervisor", "RN", "MedStaff"],
  "notification.release": ["Supervisor", "RN"],
  "restriction.set": ["Supervisor", "RN"],
  "chart.progressNote": ["Supervisor", "RN", "MedStaff"],
  "supplies.manage": ["SystemAdmin", "Supervisor", "RN", "MedStaff"],
  "supplies.dosing": ["Supervisor", "RN"],
  "staff.import": ["SystemAdmin"],
  "concern.add": ALL,
  "concern.close": ["SystemAdmin"],
  "export.phi": ["SystemAdmin", "Supervisor", "RN"],
  "reports.screen": ALL,
};

/** The subset of a user row that decides what they may do. */
export interface Actor {
  role: Role;
  licensed: boolean;
  mayAdministerOtc: boolean;
}

export function isRole(v: string): v is Role {
  return (ROLES as readonly string[]).includes(v);
}

export function can(actor: Pick<Actor, "role">, cap: Capability): boolean {
  return CAN[cap].includes(actor.role);
}

export function capabilitiesOf(actor: Pick<Actor, "role">): Capability[] {
  return CAPABILITIES.filter((c) => can(actor, c));
}

/**
 * May this user be "Administered by" for a prescription dose? Supervisor and
 * RN always; System Admin only with Licensed=Y; never MedStaff or
 * MedAdminStaff (plan §5).
 */
export function mayAdministerRx(a: Actor): boolean {
  if (a.role === "Supervisor" || a.role === "RN") return true;
  if (a.role === "SystemAdmin") return a.licensed;
  return false;
}

/**
 * May this user be "Administered by" for an OTC dose? Supervisor and RN
 * always; System Admin with Licensed or the OTC flag; MedStaff only with the
 * OTC flag (default off); never MedAdminStaff.
 */
export function mayAdministerOtc(a: Actor): boolean {
  if (a.role === "Supervisor" || a.role === "RN") return true;
  if (a.role === "SystemAdmin") return a.licensed || a.mayAdministerOtc;
  if (a.role === "MedStaff") return a.mayAdministerOtc;
  return false;
}

/** Default Licensed flag for a new account of this role (plan §5). */
export function defaultLicensed(role: Role): boolean {
  return role === "RN" || role === "Supervisor";
}
